import {
    Component,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ModalController,
    NavParams,
    ToastController,
    AlertController,
} from '@ionic/angular';
import { AjaxService } from '../ajax.service';
import { GlobalService } from '../global.service';
import { I18nService } from '../shared/i18n.service';
import { VoiceMemoPage } from '../voice-memo/voice-memo.page';
import { RecordingData, VoiceRecorder } from 'capacitor-voice-recorder';
import { DatePipe } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { UploadService } from '../upload.service';
import { Browser } from '@capacitor/browser';
import { IonModal } from '@ionic/angular/common';
import { Platform } from '@ionic/angular';
import { Keyboard } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';

@Component({
    selector: 'app-note-details',
    templateUrl: './note-details.page.html',
    styleUrls: ['./note-details.page.scss'],
    standalone: false,
})
export class NoteDetailsPage implements OnInit, OnDestroy {
    noteId: number | null = null;
    ordernr: string | null = null;
    isNew = true;
    isLoading = true;
    isUploading = false;
    pageTitle = '';

    note: any = {
        Titel: '',
        Bericht: '',
        Voor: '',
        Door: '',
        DueDate: new Date().toISOString(),
        // ? new stuffs for scheduler
        prioriteit: 5, // ?normal in lisaas backfofice
        isFixed: 0,
        startTime: new Date().toISOString(),
        endTime: new Date(new Date().getTime() + 60 * 60 * 1000).toISOString(), // ? deafult 1 hour later
        estHours: 1,
    };

    files: any[] = [];

    priorityOptions = [
        { value: 0, name: this.i18n.tr('[en]Lowest') },
        { value: 3, name: this.i18n.tr('[en]Low') },
        { value: 5, name: this.i18n.tr('[en]Normal') },
        { value: 7, name: this.i18n.tr('[en]High') },
        { value: 9, name: this.i18n.tr('[en]Highest') },
    ];

    private recordedAudio: RecordingData | null = null;
    private audioPlayer = new Audio();
    currentPlayingFile: any | null = null;
    audioProgress = 0;

    allUsers: any[] = [];

    selectedOrder: any;
    orderSuggestions: any[] = [];

    // ? new
    orderInputValue: string = '';

    public quillConfig = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
        ],
    };

    keyboardOpen = false;
    private keyboardShowListener: PluginListenerHandle | null = null;
    private keyboardHideListener: PluginListenerHandle | null = null;

    @ViewChild('dateModal') dateModal!: IonModal;

    openDateModal() {
        this.dateModal.present();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event) {
        const target = event.target as HTMLElement;
        // ?vCheck if the click is outside the autocomplete area
        if (
            !target.closest('.autocomplete-dropdown') &&
            !target.closest('ion-input')
        ) {
            this.orderSuggestions = [];
        }
    }
    constructor(
        private modalCtrl: ModalController,
        private navParams: NavParams,
        private ajax: AjaxService,
        public global: GlobalService,
        public i18n: I18nService,
        private toastCtrl: ToastController,
        private alertCtrl: AlertController,
        private uploadService: UploadService,
        private datePipe: DatePipe,
        private zone: NgZone,
        private platform: Platform
    ) {}

    async ngOnInit() {
        this.noteId = this.navParams.get('noteId');
        this.ordernr = this.navParams.get('ordernr');
        this.loadAllUsers();

        if (this.noteId) {
            this.isNew = false;
            this.pageTitle = this.i18n.tr('Aantekening bewerken');
            this.loadAllUsers()
                .then(() => {
                    this.loadNoteDetails();
                })
                .catch((error) => {
                    console.error('Failed to load prerequisites:', error);
                    this.isLoading = false;
                    this.dismiss();
                });
        } else {
            this.isNew = true;
            this.pageTitle = this.i18n.tr('Nieuwe aantekening');

            this.loadAllUsers().catch((error) => {
                console.error('Failed to initialize new note:', error);
            });
            if (this.ordernr) {
                this.selectedOrder = { id: this.ordernr, text: this.ordernr };
                this.orderInputValue = this.ordernr; // ? new
            }
        }

        this.keyboardShowListener = await Keyboard.addListener(
            'keyboardWillShow',
            () => {
                this.zone.run(() => {
                    this.keyboardOpen = true;
                });
            }
        );

        this.keyboardHideListener = await Keyboard.addListener(
            'keyboardWillHide',
            () => {
                this.zone.run(() => {
                    this.keyboardOpen = false;
                });
            }
        );

        VoiceRecorder.requestAudioRecordingPermission();
    }

    ngOnDestroy() {
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.src = '';
        }
        if (this.keyboardShowListener) {
            this.keyboardShowListener.remove();
        }
        if (this.keyboardHideListener) {
            this.keyboardHideListener.remove();
        }
    }

    onOrderInput(event: any) {
        const searchTerm = event.detail.value; //? new
        this.orderInputValue = searchTerm; // ? new
        this.selectedOrder = null; // ?new

        if (searchTerm && searchTerm.length > 1) {
            this.ajax
                .get('searchOrders', { search: searchTerm })
                .then((data: any) => {
                    // console.log(data);
                    // console.log(JSON.stringify(data));
                    if (data.status === 'OK' && data.records) {
                        this.orderSuggestions = data.records;
                    } else {
                        this.orderSuggestions = [];
                    }
                });
        } else {
            this.orderSuggestions = [];
        }
    }

    displayOrderFn(order: any): string {
        return order && order.text ? order.text : '';
    }

    selectOrder(suggestion: any) {
        this.selectedOrder = suggestion;
        this.orderInputValue = suggestion.text; // ? update the display value
        this.orderSuggestions = []; // ? hide the dropdown
    }

    getVoorUserText(): string | null {
        if (!this.note?.Voor || this.allUsers.length === 0) {
            return null; // ? placeholder show if no user is selected or list isn't loaded
        }
        const selectedUser = this.allUsers.find(
            (user) => user.id === this.note.Voor
        );
        return selectedUser ? selectedUser.text : null;
    }

    async loadAllUsers() {
        return this.ajax.get('loadUsers', {}).then((data: any) => {
            if (data.status === 'OK' && data.records) {
                this.allUsers = data.records;

                if (this.isNew) {
                    const currentUser = this.allUsers.find(
                        (user) => user.id === this.global.username
                    );

                    if (currentUser) {
                        this.note.Door = currentUser.text;
                        this.note.Voor = currentUser.id;
                    } else {
                        this.note.Door = this.global.username;
                        this.note.Voor = this.global.username;
                    }
                    this.isLoading = false;
                }
            } else {
                this.allUsers = [];
                console.error('Could not load users for dropdown.');
                this.presentToast(
                    this.i18n.tr('Kon gebruikerslijst niet laden'),
                    'danger'
                );
                if (this.isNew) {
                    this.isLoading = false;
                }
                throw new Error('Failed to load users from API.');
            }
        });
    }

    isAudioFile(file: any): boolean {
        const ext = file.ext.toLowerCase();
        return ['wav', 'mp3', 'm4a', 'ogg'].includes(ext);
    }

    getIconForFile(file: any): string {
        const ext = file.ext.toLowerCase();
        if (this.isAudioFile(file)) {
            return 'musical-notes-outline';
        }
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) {
            return 'image-outline';
        }
        if (ext === 'pdf') {
            return 'document-text-outline';
        }
        return 'document-outline';
    }
    isPlaying(file: any): boolean {
        return this.currentPlayingFile?.path === file.path;
    }

    openFile(file: any) {
        const filePath = this.global.baseurl + 'doc.php?src=' + file.path;

        if (this.isAudioFile(file)) {
            if (this.isPlaying(file)) {
                this.audioPlayer.pause();
                this.currentPlayingFile = null;
                return;
            }

            if (!this.audioPlayer.paused) {
                this.audioPlayer.pause();
            }

            this.currentPlayingFile = file;
            this.audioProgress = 0;
            this.audioPlayer.src = filePath;

            this.audioPlayer.ontimeupdate = () => {
                this.zone.run(() => {
                    if (this.audioPlayer.duration) {
                        this.audioProgress =
                            this.audioPlayer.currentTime /
                            this.audioPlayer.duration;
                    }
                });
            };
            this.audioPlayer.onended = () => {
                this.zone.run(() => {
                    this.currentPlayingFile = null;
                    this.audioProgress = 0;
                });
            };

            this.audioPlayer.play().catch((err) => {
                console.error('Error playing audio:', err);
                this.presentToast(
                    this.i18n.tr('Fout bij afspelen audio'),
                    'danger'
                );
                this.currentPlayingFile = null;
            });
        } else {
            Browser.open({ url: filePath });
        }
    }

    initializeNewNote() {
        this.note.Door = this.global.username;
        this.note.Voor = this.global.username;
        this.isLoading = false;
    }

    loadNoteDetails() {
        this.ajax
            .get('getNoteDetails', {
                id: this.noteId,
            })
            .then((res: any) => {
                if (res.status === 'OK' && res.details) {
                    this.note = res.details;
                    this.files = res.files;

                    const doorUser = this.allUsers.find(
                        (user) => user.id === this.note.Door
                    );
                    if (doorUser) {
                        this.note.Door = doorUser.text;
                    }

                    this.selectedOrder = {
                        id: this.note.LiproOrderNr,
                        text: `${this.note.LiproOrderNr}`,
                    };
                    this.orderInputValue = this.note.LiproOrderNr;
                } else {
                    this.presentToast(
                        this.i18n.tr('Fout bij laden van aantekening'),
                        'danger'
                    );
                    this.dismiss();
                }
                this.isLoading = false;
            })
            .catch(() => {
                this.isLoading = false;
                this.presentToast(
                    this.i18n.tr('Fout bij laden van aantekening'),
                    'danger'
                );
            });
    }
    uploadAudio(noteId: number) {
        if (!this.recordedAudio) return;

        const now = new Date();
        const formattedDate = now.toISOString();

        const fileName = `Audio-Aantek-${noteId}-${formattedDate}.wav`;
        const audioBlob = this.base64toBlob(
            this.recordedAudio.value.recordDataBase64,
            this.recordedAudio.value.mimeType
        );
        const formData = new FormData();

        formData.append('comp', this.global.compname);
        formData.append('usr', this.global.username);
        formData.append('pwd', this.global.password);
        formData.append('ses', this.global.session);
        formData.append('tbl', 'aantek');
        formData.append('val', noteId.toString());
        formData.append('sub', '/');
        formData.append('ordernr', this.ordernr || this.note.LiproOrderNr);
        formData.append('file', audioBlob, fileName);

        this.uploadService.upload(formData).subscribe({
            next: (event) => {
                if (event.type === HttpEventType.Response) {
                    this.presentToast(
                        this.i18n.tr('Aantekening en audio opgeslagen!'),
                        'success'
                    );
                    this.dismiss(true);
                    this.isUploading = false;
                }
            },
            error: (err) => {
                console.error('Upload failed:', err);
                this.presentToast(
                    this.i18n.tr('Fout: Kon audio niet uploaden.'),
                    'danger'
                );
                this.isUploading = false;
            },
        });
    }

    async save() {
        if (!this.selectedOrder || !this.selectedOrder.id) {
            const alert = await this.alertCtrl.create({
                header: this.i18n.tr('Ongeldige Order'),
                message: this.i18n.tr(
                    'Selecteer een geldige order uit de lijst.'
                ),
                buttons: [
                    {
                        text: this.i18n.tr('OK'),
                        role: 'cancel',
                    },
                ],
            });
            await alert.present();
            return;
        }

        if (this.orderInputValue !== this.selectedOrder.text) {
            const alert = await this.alertCtrl.create({
                header: this.i18n.tr('Ongeldige Order'),
                message: this.i18n.tr(
                    'Selecteer een geldige order uit de lijst.'
                ),
                buttons: [
                    {
                        text: this.i18n.tr('OK'),
                        role: 'cancel',
                    },
                ],
            });
            await alert.present();
            return;
        }

        if (
            !this.note.Bericht ||
            this.note.Bericht.trim() === '' ||
            this.note.Bericht === '<p><br></p>'
        ) {
            const alert = await this.alertCtrl.create({
                header: this.i18n.tr('Leeg Bericht'),
                message: this.i18n.tr(
                    'Voer een bericht in voordat je de aantekening opslaat.'
                ),
                buttons: [
                    {
                        text: this.i18n.tr('OK'),
                        role: 'cancel',
                    },
                ],
            });
            await alert.present();
            return;
        }
        if (this.isNew && !this.note.isFixed) {
            const estHours = parseFloat(this.note.estHours);
            if (isNaN(estHours) || estHours <= 0) {
                const alert = await this.alertCtrl.create({
                    header: this.i18n.tr('[en]Invalid Estimated Hours'),
                    message: this.i18n.tr(
                        '[en]Please enter a valid number greater than 0 for estimated hours.'
                    ),
                    buttons: [{ text: this.i18n.tr('OK'), role: 'cancel' }],
                });
                await alert.present();
                return;
            }
        }

        this.isUploading = true;

        const orderIdToSave = this.selectedOrder.id;

        if (this.isNew) {
            let estHours = this.note.estHours || 1; // ? currently default 30 minutes
            if (this.note.isFixed) {
                const start = new Date(this.note.startTime);
                const end = new Date(this.note.endTime);
                if (end > start) {
                    const diffMs = end.getTime() - start.getTime();
                    estHours = diffMs / (1000 * 60 * 60); // ? convert milliseconds to hours
                }
            }
            const params = {
                ordernr: orderIdToSave,
                title: this.note.Titel,
                bericht: this.note.Bericht,
                user: this.global.username,
                voor: this.note.Voor,
                // dueDate: this.note.DueDate,
                dueDate: this.datePipe.transform(
                    this.note.DueDate,
                    'yyyy-MM-dd'
                ),
                prioriteit: this.note.prioriteit,
                isFixed: this.note.isFixed ? 1 : 0,
                estHours: estHours,
                startTime: this.note.isFixed
                    ? this.datePipe.transform(this.note.startTime, 'HH:mm')
                    : null,
                endTime: this.note.isFixed
                    ? this.datePipe.transform(this.note.endTime, 'HH:mm')
                    : null,
            };
            // const params = {
            //     ordernr: orderIdToSave,
            //     title: this.note.Titel,
            //     bericht: this.note.Bericht,
            //     user: this.global.username,
            //     voor: this.note.Voor,
            //     dueDate: this.note.DueDate,
            // };
            // this.ajax.get('createNote', params).then((res: any) => {
            this.ajax.get('createNoteExperimental', params).then((res: any) => {
                if (res.status === 'OK' && res.new_note_id) {
                    if (this.recordedAudio) {
                        this.uploadAudio(res.new_note_id);
                    } else {
                        this.presentToast(
                            this.i18n.tr('Aantekening opgeslagen'),
                            'success'
                        );
                        this.dismiss(true);
                        this.isUploading = false;
                    }
                } else {
                    console.log(
                        `FAILED FROM CREATE NOTE\n ${JSON.stringify(res)} `
                    );

                    this.presentToast(
                        this.i18n.tr('Fout bij opslaan'),
                        'danger'
                    );
                    this.isUploading = false;
                }
            });
        } else {
            const fieldsToSave = [
                { fld: 'ID', value: this.note.ID },
                { fld: 'LiproOrderNr', value: orderIdToSave },
                { fld: 'Titel', value: this.note.Titel },
                { fld: 'Bericht', value: this.note.Bericht },
                { fld: 'Voor', value: this.note.Voor },
                { fld: 'DueDate', value: this.note.DueDate },
            ];
            this.ajax
                .get('recordSave', {
                    table: 'aantek',
                    id: this.note.ID,
                    fields: fieldsToSave,
                })
                .then((res: any) => {
                    if (res.status === 'OK') {
                        if (this.recordedAudio) {
                            this.uploadAudio(this.note.ID);
                        } else {
                            this.presentToast(
                                this.i18n.tr('Wijzigingen opgeslagen'),
                                'success'
                            );
                            this.dismiss(true);
                            this.isUploading = false;
                        }
                    } else {
                        console.log(
                            `FAILED FROM RECORD SAVE\n ${JSON.stringify(res)} `
                        );
                        this.presentToast(
                            this.i18n.tr('Fout bij opslaan'),
                            'danger'
                        );
                        this.isUploading = false;
                    }
                });
        }
    }

    async openVoiceMemoModal() {
        const modal = await this.modalCtrl.create({ component: VoiceMemoPage });
        await modal.present();
        const { data, role } = await modal.onDidDismiss();

        if (role === 'confirm' && data?.text) {
            this.recordedAudio = data.audio;
            const existingBericht = this.note.Bericht || '';
            this.note.Bericht = existingBericht.trim()
                ? `${existingBericht}<p>${data.text}</p>`
                : `<p>${data.text}</p>`;
            if (!this.note.Titel || this.note.Titel.trim() === '') {
                const now = new Date();
                // const formattedDate = this.datePipe.transform(
                //     now,
                //     'dd-MM-yy-HH-mm-ss'
                // );
                const formattedDate = now.toISOString();
                this.note.Titel = `${this.i18n.tr(
                    'Audio Memo'
                )} - ${formattedDate}`;
            }
        }
    }

    playFile(file: any) {
        const filePath = this.global.baseurl + 'doc.php?src=' + file.path;

        if (this.audioPlayer.src === filePath && !this.audioPlayer.paused) {
            this.audioPlayer.pause();
        } else {
            this.audioPlayer.src = filePath;
            this.audioPlayer.play().catch((err) => {
                console.error('Error playing audio:', err);
                this.presentToast(
                    this.i18n.tr('Fout bij afspelen audio'),
                    'danger'
                );
            });
        }
    }

    async dismiss(dataChanged = false) {
        // ? if data might have been changed, show confirmation dialog
        if (!dataChanged && this.hasUnsavedChanges()) {
            const alert = await this.alertCtrl.create({
                header: this.i18n.tr('Wijzigingen wegwerpen?'),
                message: this.i18n.tr(
                    'Er zijn niet-opgeslagen wijzigingen. Weet je zeker dat je wilt sluiten?'
                ),
                buttons: [
                    {
                        text: this.i18n.tr('Annuleren'),
                        role: 'cancel',
                    },
                    {
                        text: this.i18n.tr('Wegwerpen'),
                        role: 'destructive',
                        handler: () => {
                            this.modalCtrl.dismiss({ dataChanged: false });
                        },
                    },
                ],
            });
            await alert.present();
        } else {
            this.modalCtrl.dismiss({ dataChanged });
        }
    }

    private hasUnsavedChanges(): boolean {
        if (this.isNew) {
            return !!(
                this.note.Titel?.trim() ||
                this.note.Bericht?.trim() ||
                this.selectedOrder ||
                this.recordedAudio
            );
        } else {
            // TODO : for  existing notes,  implement more sophisticated change detection
            // return true;
            // TODO: rn assume there are no changes if the user opened the modal
            return false;
        }
    }

    base64toBlob(base64Data: string, contentType = '', sliceSize = 512): Blob {
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        for (
            let offset = 0;
            offset < byteCharacters.length;
            offset += sliceSize
        ) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    }

    async presentToast(message: string, color: 'success' | 'danger') {
        const toast = await this.toastCtrl.create({
            message,
            duration: 3000,
            color,
            position: 'bottom',
        });
        toast.present();
    }

    onDateChange() {
        this.dateModal.dismiss();
    }
}
